// Club Penguin Clone
// Owns the local penguin (movement, collision, hotspots, dialogue triggers)
// and delegates multiplayer rendering to MultiplayerManager, chat input to
// ChatInput, and dialogue display to DialogueSystem.

import { ImagePreloader } from '../utils/ImagePreloader.js';
import { DebugPanel } from './DebugPanel.js';
import { DialogueSystem } from './DialogueSystem.js';
import { MusicManager } from './MusicManager.js';
import { StateManager } from './StateManager.js';
import { MultiplayerManager } from './MultiplayerManager.js';
import { ChatInput } from './ChatInput.js';
import { rooms } from '../rooms/index.js';
import gameSocketClient from '../utils/game-websocket-client.js';
import type { PlayerDirection } from '../utils/game-websocket-client.js';
import {
  Rooms,
  Hotspot,
  InteractiveElement,
  DialogueOption,
  DialogueCondition,
  Collider,
} from '../config/room-types.js';

interface PenguinState {
  x: number;
  y: number;
  radius: number;
  speed: number;
  vx: number;
  vy: number;
  isWalking: boolean;
  facing: PlayerDirection;
}

interface Target {
  x: number;
  y: number;
}

interface CollisionResult {
  overlapping: boolean;
  pushX: number;
  pushY: number;
}

const PENGUIN_WALKING_GIF = '/media/club-penguin/penguin-avatar1.gif';
const PENGUIN_SIZE = 48;
const HOTSPOT_COOLDOWN_MS = 2000;
const MOVE_SEND_INTERVAL_MS = 100;

export class ClubPenguinGame {
  private stage: HTMLElement;
  private penguin: HTMLElement;
  private debugPanel: DebugPanel;
  private dialogueSystem: DialogueSystem;
  private musicManager: MusicManager;
  private stateManager: StateManager;
  private multiplayer: MultiplayerManager;
  private chatInput: ChatInput;

  private penguinState: PenguinState;
  private currentRoom: string;
  private target: Target | null;
  private lastTime: number;
  private rooms: Rooms;
  private lastHotspotTrigger = 0;
  private lastTriggeredHotspot: string | null = null;

  private lastMoveSent = 0;
  private lastSentWalking = false;

  private idleImageUrl: string = PENGUIN_WALKING_GIF;

  constructor() {
    this.stage = document.getElementById('stage')!;
    this.penguin = document.getElementById('penguin')!;

    this.debugPanel = new DebugPanel({
      stage: this.stage,
      onRectanglesChange: () => {},
    });

    this.dialogueSystem = new DialogueSystem();
    this.musicManager = new MusicManager(1);
    this.stateManager = new StateManager();

    gameSocketClient.connect();
    this.multiplayer = new MultiplayerManager(this.stage, gameSocketClient, {
      idle: this.idleImageUrl,
      walking: PENGUIN_WALKING_GIF,
    });
    this.chatInput = new ChatInput(message => this.sendChatMessage(message));

    this.penguinState = {
      x: 480,
      y: 400,
      radius: 20,
      speed: 180, // pixels per second
      vx: 0,
      vy: 0,
      isWalking: false,
      facing: 'right',
    };

    this.currentRoom = 'beach';
    this.target = null;
    this.lastTime = performance.now();
    this.rooms = rooms;

    void this.init();
  }

  private async init(): Promise<void> {
    await this.preloadGameImages();
    this.idleImageUrl = await createStaticFrame(PENGUIN_WALKING_GIF, PENGUIN_SIZE);
    this.multiplayer.setSprites({ idle: this.idleImageUrl, walking: PENGUIN_WALKING_GIF });

    this.penguin.style.backgroundImage = `url("${this.idleImageUrl}")`;

    await this.loadRoom(this.currentRoom);
    this.setupEventListeners();
    this.gameLoop();
  }

  private async preloadGameImages(): Promise<void> {
    const imagePaths = new Set<string>([PENGUIN_WALKING_GIF]);
    Object.values(this.rooms).forEach(room => imagePaths.add(room.background));

    try {
      await new ImagePreloader([...imagePaths]).preload();
    } catch (error) {
      console.warn('Some images failed to preload:', error);
    }
  }

  private setupEventListeners(): void {
    this.stage.addEventListener('click', (e: MouseEvent) => this.handleStageClick(e));

    // Debug toggle. Chat hotkeys ('T', Escape) are owned by ChatInput.
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (this.chatInput.isFocused()) return;

      if (e.key.toLowerCase() === 'd') {
        document.body.classList.toggle('debug-mode');
        this.stage.classList.toggle('debug-mode');
        this.toggleDebugMode();
      }
    });
  }

  private handleStageClick(e: MouseEvent): void {
    const rect = this.stage.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (this.debugPanel.handleStageClick(x, y)) {
      return;
    }

    this.target = { x, y };
  }

  private async executeHotspotAction(
    action: string,
    target: string,
    hotspot: Hotspot,
    isFirstInteraction: boolean = false,
  ): Promise<void> {
    if (action === 'goto' && target) {
      await this.loadRoom(target, hotspot.spawn);
    } else if (action === 'dialogue') {
      this.handleDialogueHotspot(hotspot, isFirstInteraction);
    }
  }

  private handleDialogueHotspot(hotspot: Hotspot, isFirstInteraction: boolean): void {
    if (!hotspot.dialogueOptions || hotspot.dialogueOptions.length === 0) {
      console.warn(`Dialogue hotspot ${hotspot.id} has no dialogue options`);
      return;
    }

    const option = hotspot.dialogueOptions.find(o =>
      this.evaluateDialogueCondition(o.condition, isFirstInteraction),
    );
    if (option) {
      this.showDialogueOption(option, hotspot);
    }
  }

  private evaluateDialogueCondition(
    condition: DialogueCondition,
    isFirstInteraction: boolean,
  ): boolean {
    switch (condition.type) {
      case 'always':
        return true;
      case 'first_interaction':
        return isFirstInteraction;
      case 'flag_exists':
        return condition.key ? this.stateManager.hasFlag(condition.key) : false;
      case 'flag_equals':
        return condition.key ? this.stateManager.getFlag(condition.key) === condition.value : false;
      case 'room_visited':
        return condition.roomId ? this.stateManager.hasVisitedRoom(condition.roomId) : false;
      default:
        console.warn(`Unknown dialogue condition type: ${condition.type as string}`);
        return false;
    }
  }

  private showDialogueOption(option: DialogueOption, hotspot: Hotspot): void {
    const x = option.position?.x ?? hotspot.x + hotspot.w / 2;
    const y = option.position?.y ?? hotspot.y - 20;
    const onComplete = () => this.handleDialogueCompletion(option);

    if (Array.isArray(option.text)) {
      this.dialogueSystem.showConversation(option.text, onComplete);
    } else {
      this.dialogueSystem.showMessageAt('', option.text, x, y, onComplete);
    }
  }

  private handleDialogueCompletion(option: DialogueOption): void {
    const actions = option.onComplete;
    if (!actions) return;

    if (actions.setFlag) {
      this.stateManager.setFlag(actions.setFlag.key, actions.setFlag.value);
    }
    if (actions.completeAction) {
      this.stateManager.completeAction(actions.completeAction);
    }
    if (actions.gotoRoom) {
      const { roomId, spawn } = actions.gotoRoom;
      // Small delay so the closing dialogue doesn't immediately re-trigger a hotspot
      setTimeout(() => void this.loadRoom(roomId, spawn ?? null), 500);
    }
  }

  private async loadRoom(
    roomId: string,
    spawnPoint: { x: number; y: number } | null = null,
  ): Promise<void> {
    const room = this.rooms[roomId];
    if (!room) {
      console.error(`Room ${roomId} not found!`);
      return;
    }

    this.currentRoom = roomId;

    const spawn = spawnPoint || room.spawn;
    this.penguinState.x = spawn.x;
    this.penguinState.y = spawn.y;
    this.penguinState.vx = 0;
    this.penguinState.vy = 0;
    this.target = null;

    // Joining implicitly leaves the previous room server-side; the server
    // answers with a room snapshot that repopulates the remote players.
    this.multiplayer.reset();
    gameSocketClient.joinRoom(roomId, spawn.x, spawn.y);

    this.stage.style.backgroundImage = `url("${room.background}")`;
    this.debugPanel.updateRoom(roomId, room.name);
    this.stateManager.visitRoom(roomId);

    this.clearRoomElements();
    this.createHotspots(room.hotspots);

    if (room.interactiveElements) {
      this.injectInteractiveCallbacks(room.interactiveElements, roomId);
      this.createInteractiveElements(room.interactiveElements);
    }

    if (room.music) {
      this.musicManager.playMusic(room.music);
    } else {
      this.musicManager.stopMusic();
    }

    if (this.stage.classList.contains('debug-mode')) {
      this.createDebugColliders(room.colliders);
    }

    this.updatePenguinPosition();
  }

  private clearRoomElements(): void {
    this.stage.querySelectorAll('.hotspot').forEach(el => el.remove());
    this.stage.querySelectorAll('.interactive-element').forEach(el => el.remove());
    this.stage.querySelectorAll('.collider').forEach(el => el.remove());
  }

  private createHotspots(hotspots: Hotspot[]): void {
    hotspots.forEach(hotspot => {
      const el = document.createElement('div');
      el.className = 'hotspot';
      el.style.left = hotspot.x + 'px';
      el.style.top = hotspot.y + 'px';
      el.style.width = hotspot.w + 'px';
      el.style.height = hotspot.h + 'px';
      el.title = `Go to ${hotspot.target}`;
      this.stage.appendChild(el);
    });
  }

  private createDebugColliders(colliders: Collider[]): void {
    colliders.forEach(collider => {
      const el = document.createElement('div');
      el.className = 'collider';
      el.style.left = collider.x + 'px';
      el.style.top = collider.y + 'px';
      el.style.width = collider.w + 'px';
      el.style.height = collider.h + 'px';
      this.stage.appendChild(el);
    });
  }

  private gameLoop(): void {
    const now = performance.now();
    const deltaTime = Math.min(0.033, (now - this.lastTime) / 1000);
    this.lastTime = now;

    this.updateMovement(deltaTime);
    this.updatePenguinPosition();
    this.debugPanel.updatePosition(this.penguinState.x, this.penguinState.y);

    requestAnimationFrame(() => this.gameLoop());
  }

  private updateMovement(deltaTime: number): void {
    if (this.target) {
      const dx = this.target.x - this.penguinState.x;
      const dy = this.target.y - this.penguinState.y;
      const distance = Math.hypot(dx, dy);

      if (distance < 5) {
        this.penguinState.vx = 0;
        this.penguinState.vy = 0;
        this.target = null;
        this.penguinState.isWalking = false;
      } else {
        this.penguinState.vx = (dx / distance) * this.penguinState.speed;
        this.penguinState.vy = (dy / distance) * this.penguinState.speed;
        this.penguinState.isWalking = true;
      }
    } else {
      this.penguinState.vx = 0;
      this.penguinState.vy = 0;
      this.penguinState.isWalking = false;
    }

    if (this.penguinState.vx > 0) {
      this.penguinState.facing = 'right';
    } else if (this.penguinState.vx < 0) {
      this.penguinState.facing = 'left';
    }

    this.penguinState.x += this.penguinState.vx * deltaTime;
    this.penguinState.y += this.penguinState.vy * deltaTime;

    this.handleCollisions();
    this.checkHotspotCollisions();
    this.sendMovementUpdate();
  }

  private sendMovementUpdate(): void {
    const now = Date.now();
    const { x, y, facing, isWalking } = this.penguinState;
    const stoppedThisFrame = this.lastSentWalking && !isWalking;

    // While walking, send at a fixed interval; when we stop, send one final
    // update immediately so remote penguins come to rest at the right spot.
    if (stoppedThisFrame || (isWalking && now - this.lastMoveSent >= MOVE_SEND_INTERVAL_MS)) {
      this.lastMoveSent = now;
      this.lastSentWalking = isWalking;
      gameSocketClient.sendMove(x, y, facing, isWalking);
    }
  }

  private handleCollisions(): void {
    const room = this.rooms[this.currentRoom];
    if (!room) return;

    for (const collider of room.colliders) {
      const collision = this.checkCircleRectCollision(
        this.penguinState.x,
        this.penguinState.y,
        this.penguinState.radius,
        collider.x,
        collider.y,
        collider.w,
        collider.h,
      );

      if (collision.overlapping) {
        this.penguinState.x += collision.pushX;
        this.penguinState.y += collision.pushY;

        if (Math.abs(collision.pushX) > Math.abs(collision.pushY)) {
          this.penguinState.vx = 0;
        } else {
          this.penguinState.vy = 0;
        }
      }
    }
  }

  private checkHotspotCollisions(): void {
    const room = this.rooms[this.currentRoom];
    if (!room) return;

    const now = performance.now();
    if (now - this.lastHotspotTrigger < HOTSPOT_COOLDOWN_MS) {
      return;
    }

    for (const hotspot of room.hotspots) {
      const collision = this.checkCircleRectCollision(
        this.penguinState.x,
        this.penguinState.y,
        this.penguinState.radius,
        hotspot.x,
        hotspot.y,
        hotspot.w,
        hotspot.h,
      );

      if (collision.overlapping) {
        if (this.lastTriggeredHotspot === hotspot.id && hotspot.id) {
          return;
        }

        this.lastHotspotTrigger = now;
        this.lastTriggeredHotspot = hotspot.id || null;

        let isFirstInteraction = false;
        if (hotspot.id) {
          isFirstInteraction = this.stateManager.interactWithHotspot(hotspot.id);
        }

        void this.executeHotspotAction(hotspot.action, hotspot.target, hotspot, isFirstInteraction);
        return; // Only trigger one hotspot at a time
      }
    }

    this.lastTriggeredHotspot = null;
  }

  private checkCircleRectCollision(
    cx: number,
    cy: number,
    radius: number,
    rx: number,
    ry: number,
    rw: number,
    rh: number,
  ): CollisionResult {
    const closestX = Math.max(rx, Math.min(cx, rx + rw));
    const closestY = Math.max(ry, Math.min(cy, ry + rh));

    const dx = cx - closestX;
    const dy = cy - closestY;
    const distanceSquared = dx * dx + dy * dy;

    if (distanceSquared < radius * radius) {
      const distance = Math.sqrt(distanceSquared);
      const penetration = radius - distance;

      if (distance === 0) {
        // Circle center is exactly on the rectangle edge
        return { overlapping: true, pushX: radius, pushY: 0 };
      }

      return {
        overlapping: true,
        pushX: (dx / distance) * penetration,
        pushY: (dy / distance) * penetration,
      };
    }

    return { overlapping: false, pushX: 0, pushY: 0 };
  }

  private updatePenguinPosition(): void {
    this.penguin.style.left = this.penguinState.x + 'px';
    this.penguin.style.top = this.penguinState.y + 'px';

    if (this.penguinState.isWalking) {
      this.penguin.classList.add('walking');
      this.penguin.classList.remove('idle');
      this.penguin.style.backgroundImage = `url("${PENGUIN_WALKING_GIF}")`;
      const flip = this.penguinState.facing === 'left' ? -1 : 1;
      this.penguin.style.transform = `translate(-50%, -50%) scaleX(${flip})`;
    } else {
      this.penguin.classList.add('idle');
      this.penguin.classList.remove('walking');
      this.penguin.style.backgroundImage = `url("${this.idleImageUrl}")`;
    }

    this.multiplayer.syncLocalBubble(this.penguinState.x, this.penguinState.y);
  }

  private toggleDebugMode(): void {
    const room = this.rooms[this.currentRoom];
    if (!room) return;

    if (this.stage.classList.contains('debug-mode')) {
      this.createDebugColliders(room.colliders);
    } else {
      this.stage.querySelectorAll('.collider').forEach(el => el.remove());
    }
  }

  private sendChatMessage(message: string): void {
    gameSocketClient.sendChat(message);
    this.multiplayer.showLocalBubble(message, this.penguinState.x, this.penguinState.y);
  }

  private injectInteractiveCallbacks(elements: InteractiveElement[], roomId: string): void {
    elements.forEach(element => {
      if (element.id === 'town-info-button' && roomId === 'town') {
        element.callback = () => {
          this.dialogueSystem.showMessageAt(
            '',
            'Welcome to the Town!\nThis is the central hub where all penguins gather.',
            450,
            300,
          );
        };
      }
      // Add more callback injections here for other interactive elements
    });
  }

  private createInteractiveElements(elements: InteractiveElement[]): void {
    elements.forEach(element => {
      const el = document.createElement(element.type === 'button' ? 'button' : 'div');
      el.className = `interactive-element ${element.type}`;
      el.style.left = element.x + 'px';
      el.style.top = element.y + 'px';
      el.style.width = element.w + 'px';
      el.style.height = element.h + 'px';

      if (element.text && element.type === 'button') {
        el.textContent = element.text;
      }

      el.addEventListener('click', e => {
        e.stopPropagation();
        element.callback();
      });

      this.stage.appendChild(el);
    });
  }

  // Public debug API (used by window.debugGame)
  public teleport(x: number, y: number): void {
    this.penguinState.x = x;
    this.penguinState.y = y;
    this.target = null;
  }

  public gotoRoom(roomId: string, x?: number, y?: number): void {
    if (this.rooms[roomId]) {
      const spawn = x !== undefined && y !== undefined ? { x, y } : null;
      void this.loadRoom(roomId, spawn);
    }
  }

  public listRooms(): string[] {
    return Object.keys(this.rooms);
  }

  public getDialogueSystem(): DialogueSystem {
    return this.dialogueSystem;
  }

  public getMusicManager(): MusicManager {
    return this.musicManager;
  }

  public getStateManager(): StateManager {
    return this.stateManager;
  }
}

/**
 * Extract the first frame of a GIF as a static data-URL image.
 * Falls back to the GIF itself if the frame can't be extracted.
 */
function createStaticFrame(gifUrl: string, size: number): Promise<string> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        canvas.getContext('2d')!.drawImage(img, 0, 0, size, size);
        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        console.warn('Could not create static penguin frame, using GIF:', error);
        resolve(gifUrl);
      }
    };
    img.onerror = () => resolve(gifUrl);
    img.src = gifUrl;
  });
}
